// app_client/src/pages/admin/InvoiceManagement.jsx
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import useInvoiceStore from '../../store/useInvoiceStore';
import { 
  FilePlus, Search, CheckCircle2, XCircle, Clock,
  Ban, Trash2
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import Pagination from '../../components/common/Pagination';
import { useConfirmation } from '../../components/providers/ConfirmationProvider';
import useListQuery from '../../hooks/useListQuery';
import { formatCurrency } from '../../utils/formatters';

const invoiceTypeLabels = {
  all: 'Tümü',
  Sales: 'Satış',
  Purchase: 'Alış',
  SalesReturn: 'Satış İade',
  PurchaseReturn: 'Alış İade'
};

const InvoiceManagement = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { confirm } = useConfirmation();
  const { invoices, pagination, fetchInvoiceDependencies, deleteInvoice, cancelInvoice } = useInvoiceStore();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [customerSearch, setCustomerSearch] = useState('');
  const [activeType, setActiveType] = useState('all');
  const [paymentStatus, setPaymentStatus] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [page, setPage] = useState(1);

  const { refresh, loading } = useListQuery({
    store: useInvoiceStore,
    fetchAction: 'fetchInvoices',
    filters: {
      search: searchTerm,
      customer: customerSearch,
      type: activeType === 'all' ? undefined : activeType,
      paymentStatus: paymentStatus === 'all' ? undefined : paymentStatus,
      startDate,
      endDate,
      limit: 10
    },
    page,
    debounceMs: 500
  });

  useEffect(() => {
    if (location.state?.refreshList) {
      refresh();
    }
  }, [location.state, refresh]);

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const buildDependencyWarning = (dependencies) => {
    const transactionCount = dependencies?.counts?.transactions || 0;
    const operationCount = dependencies?.counts?.operations || 0;
    const lines = [];

    if (operationCount > 0) {
      const operationNames = (dependencies.operations || [])
        .map((operation) => operation.projectNo || operation.name)
        .filter(Boolean)
        .join(', ');
      lines.push(`Bu fatura ${operationCount} proje ile bağlantılı${operationNames ? `: ${operationNames}` : '.'}`);
    }

    if (transactionCount > 0) {
      lines.push(`Bu faturaya bağlı ${transactionCount} tahsilat/ödeme hareketi var. Faturayı silmeden önce ilgili tahsilatları silmelisiniz.`);
    }

    return lines.join('\n');
  };

  const handleDeleteInvoice = async (event, invoice) => {
    event.stopPropagation();
    if (Number(invoice.paidAmount || 0) > 0 || ['Paid', 'PartiallyPaid'].includes(invoice.paymentStatus)) {
      toast.error('Bu faturaya ait işlenmiş ödeme/tahsilat bulunmaktadır. Değişiklik yapmadan veya silmeden önce lütfen ilgili finansal eşleştirmeleri kaldırınız.');
      return;
    }

    const dependenciesResult = await fetchInvoiceDependencies(invoice._id);
    if (!dependenciesResult.success) {
      toast.error(dependenciesResult.message);
      return;
    }

    const dependencies = dependenciesResult.data || {};
    const transactionCount = dependencies?.counts?.transactions || 0;
    const specialWarning = buildDependencyWarning(dependencies);

    if (transactionCount > 0) {
      const decision = await confirm({
        title: 'Fatura Silinemez',
        message: `${invoice.invoiceNumber} numaralı faturanın bağlı tahsilatları bulunduğu için silme işlemi engellendi.`,
        specialWarning,
        confirmText: 'Tahsilatlara Git',
        cancelText: 'Kapat',
        confirmValue: 'transactions',
        cancelValue: false,
        dismissValue: false
      });

      if (decision === 'transactions') {
        navigate('/transactions');
      }
      return;
    }

    const confirmed = await confirm({
      title: 'Faturayı Sil',
      message: 'Bu faturayı tamamen silmek istiyor musunuz?',
      specialWarning,
      confirmText: 'Sil',
      cancelText: 'Vazgeç',
      requireReason: true,
      reasonLabel: 'Silme nedeni',
      reasonPlaceholder: 'Örn: Hatalı kayıt, mükerrer fatura...'
    });

    if (!confirmed) return;

    const res = await deleteInvoice(invoice._id, confirmed.reason);
    if (res.success) {
      toast.success('Fatura silindi.');
    } else {
      toast.error(res.message || 'Fatura silinemedi.', { duration: 6000 });
    }
  };

  const handleCancelInvoice = async (event, invoice) => {
    event.stopPropagation();

    const confirmed = await confirm({
      title: 'Faturayı İptal Et',
      message: 'Bu faturayı iptal etmek istediğinize emin misiniz?',
      confirmText: 'İptal Et',
      cancelText: 'Vazgeç',
      confirmValue: true,
      cancelValue: false,
      dismissValue: false
    });

    if (!confirmed) return;

    const res = await cancelInvoice(invoice._id);
    if (res.success) {
      toast.success('Fatura iptal edildi.');
    } else {
      toast.error(res.message || 'Fatura iptal edilemedi.', { duration: 6000 });
    }
  };

  const getTypeStyle = (type) => {
    const styles = {
      Sales: "bg-blue-50 text-blue-600 border-blue-100",
      Purchase: "bg-purple-50 text-purple-600 border-purple-100",
      SalesReturn: "bg-rose-50 text-rose-600 border-rose-100",
      PurchaseReturn: "bg-orange-50 text-orange-600 border-orange-100"
    };
    return styles[type] || "bg-slate-50 text-slate-600";
  };

  const getPaymentStatusBadge = (paymentStatus) => {
    const styles = {
      Paid: 'bg-emerald-50 text-emerald-700 border-emerald-100',
      PartiallyPaid: 'bg-orange-50 text-orange-700 border-orange-100',
      Unpaid: 'bg-rose-50 text-rose-700 border-rose-100'
    };

    const labels = {
      Paid: 'Ödendi',
      PartiallyPaid: 'Kısmi',
      Unpaid: 'Ödenmedi'
    };

    return (
      <span className={`inline-flex items-center justify-center px-3 py-1 rounded-lg text-[10px] font-black border whitespace-nowrap ${styles[paymentStatus] || styles.Unpaid}`}>
        {labels[paymentStatus] || 'Ödenmedi'}
      </span>
    );
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 bg-slate-50/50 p-4 rounded-4xl border border-slate-100">
        <div>
          <h1 className="text-2xl font-black text-slate-800 tracking-tight">Fatura Yönetimi</h1>
          <p className="text-slate-500 text-sm">Satış, alış ve iade kayıtları</p>
        </div>

        <div className="flex items-center gap-3">
          <div className="relative group">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
            <input 
              type="text" placeholder="Fatura no veya cari ara..."
              className="pl-11 pr-4 py-3 rounded-2xl border border-transparent bg-white shadow-sm focus:ring-4 focus:ring-blue-500/10 outline-none text-sm font-bold"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(1);
              }}
            />
          </div>
          <button onClick={() => navigate('/invoices/new')} className="bg-slate-900 hover:bg-black text-white px-6 py-3 rounded-2xl font-black text-xs uppercase tracking-widest transition-all flex items-center gap-2">
            <FilePlus size={18} /> Yeni Fatura
          </button>
        </div>
      </div>

      {/* Advanced Filters */}
      <div className="bg-white p-4 rounded-3xl border border-slate-100 shadow-sm flex flex-wrap items-center gap-4">
        <input
          type="text"
          value={customerSearch}
          onChange={(e) => {
            setCustomerSearch(e.target.value);
            setPage(1);
          }}
          placeholder="Cari / müşteri ara..."
          className="bg-slate-50 rounded-xl px-4 py-2 text-xs font-bold text-slate-600 outline-none border border-slate-100"
        />
        <input
          type="date"
          value={startDate}
          onChange={(e) => {
            setStartDate(e.target.value);
            setPage(1);
          }}
          className="bg-slate-50 rounded-xl px-4 py-2 text-xs font-bold text-slate-600 outline-none border border-slate-100"
        />
        <input
          type="date"
          value={endDate}
          onChange={(e) => {
            setEndDate(e.target.value);
            setPage(1);
          }}
          className="bg-slate-50 rounded-xl px-4 py-2 text-xs font-bold text-slate-600 outline-none border border-slate-100"
        />
        <select
          value={paymentStatus}
          onChange={(e) => {
            setPaymentStatus(e.target.value);
            setPage(1);
          }}
          className="bg-slate-50 rounded-xl px-4 py-2 text-xs font-bold text-slate-600 outline-none border border-slate-100"
        >
          <option value="all">Tüm Ödemeler</option>
          <option value="Paid">Ödendi</option>
          <option value="PartiallyPaid">Kısmi</option>
          <option value="Unpaid">Ödenmedi</option>
        </select>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2 p-1 bg-slate-100 w-fit rounded-2xl border border-slate-200 overflow-x-auto">
        {['all', 'Sales', 'Purchase', 'SalesReturn', 'PurchaseReturn'].map(type => (
          <button
            key={type}
            onClick={() => {
              setActiveType(type);
              setPage(1);
            }}
            className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-tighter transition-all whitespace-nowrap ${activeType === type ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
          >
            {invoiceTypeLabels[type] || type}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="bg-white rounded-[2.5rem] shadow-sm border border-slate-100 overflow-hidden relative">
        {loading && <div className="absolute inset-0 bg-white/50 backdrop-blur-[1px] z-10 flex items-center justify-center font-bold text-slate-400">Yükleniyor...</div>}
        
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-slate-50 text-[10px] font-black text-slate-400 uppercase tracking-widest">
                <th className="p-6">Numarası</th>
                <th className="p-6">Cari Hesap</th>
                <th className="p-6">Tür</th>
                <th className="p-6 text-right">Toplam Tutar</th>
                <th className="p-6 text-right">Ödenen</th>
                <th className="p-6 text-right">Kalan Bakiye</th>
                <th className="p-6 text-center">Ödeme Durumu</th>
                <th className="p-6 text-right">Aksiyon</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {invoices.map((inv) => (
                <tr key={inv._id} className="hover:bg-slate-50/80 transition-all group cursor-pointer" onClick={() => navigate(`/invoices/${inv._id}`)}>
                  <td className="p-6">
                    <div className="flex flex-col">
                      <span className="font-black text-slate-800">{inv.invoiceNumber}</span>
                      <span className="text-[10px] font-bold text-slate-400">{new Date(inv.date).toLocaleDateString('tr-TR')}</span>
                    </div>
                  </td>
                  <td className="p-6 font-bold text-slate-600">{inv.accountId?.name || '---'}</td>
                  <td className="p-6">
                    <span className={`px-3 py-1 rounded-lg text-[10px] font-black border ${getTypeStyle(inv.type)}`}>
                      {invoiceTypeLabels[inv.type] || inv.type}
                    </span>
                  </td>
                  <td className="p-6 text-right font-black text-slate-800">
                    {formatCurrency(inv.grandTotal, inv.currency)}
                  </td>
                  <td className="p-6 text-right font-black text-emerald-600">
                    {formatCurrency(inv.paidAmount, inv.currency)}
                  </td>
                  <td className="p-6 text-right font-black text-rose-600">
                    {formatCurrency(inv.remainingAmount, inv.currency)}
                  </td>
                  <td className="p-6 text-center">
                    {getPaymentStatusBadge(inv.paymentStatus)}
                  </td>
                  <td className="p-6">
                    <div className="flex items-center justify-end gap-2">
                      {inv.status !== 'Cancelled' && (
                        <button
                          type="button"
                          onClick={(event) => handleCancelInvoice(event, inv)}
                          className="inline-flex h-10 w-10 items-center justify-center rounded-xl text-slate-400 hover:bg-amber-50 hover:text-amber-600 transition-colors"
                          title="Faturayı iptal et"
                          aria-label="Faturayı iptal et"
                        >
                          <Ban size={18} />
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={(event) => handleDeleteInvoice(event, inv)}
                        className="inline-flex h-10 w-10 items-center justify-center rounded-xl text-slate-400 hover:bg-rose-50 hover:text-rose-600 transition-colors"
                        title="Faturayı sil"
                        aria-label="Faturayı sil"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination 
          currentPage={page} 
          totalPages={pagination.totalPages} 
          onPageChange={handlePageChange} 
        />
      </div>
    </div>
  );
};

export default InvoiceManagement;
